import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { css } from '@emotion/react';
import { color } from '../../../../styles';
import { IGetDfreeBDeviceAnalyticsResult } from '../../../../types/api/traversal/getDfreeBDeviceAnalytics';
import { DEFECATION_ANALYTICS } from '../../../../constants/defecationAnalytics';
import { useChartResizeObserver } from '../../../../hooks/useChartResizeObserver';
import { useTranslation } from 'react-i18next';

interface Props {
  countFecesByTerm: IGetDfreeBDeviceAnalyticsResult['countFecesByTerm'];
}

const GraphContainerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 16px;
  border-radius: 8px;
  border: 1px solid ${color.border_gray};
  background: ${color.light_gray};
`;

const GraphTitle = css`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 16px;
`;

const GraphSubtitle = css`
  margin-bottom: 12px;
`;

const GraphLabel = css`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

// DayNightCounterComponent
const CounterContainer = css`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  padding: 24px;
  border: 1px solid #ccc;
  border-radius: 8px;
  position: relative;
  background-color: #fff;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);

  &::before {
    content: '';
    position: absolute;
    top: -32px;
    left: 50%;
    transform: translateX(-50%);
    border-width: 16px;
    border-style: solid;
    border-color: transparent transparent ${color.border_gray} transparent;
  }

  &::after {
    content: '';
    position: absolute;
    top: -32px;
    left: 50%;
    transform: translateX(-50%);
    border-width: 17px;
    border-style: solid;
    border-color: transparent transparent white transparent;
  }
`;

const CounterSection = css`
  display: flex;
  align-items: center;
`;

const CounterLabel = (lang: string) => css`
  display: flex;
  flex-direction: column;
  align-items: center;
  writing-mode: ${lang === 'ja' ? 'vertical-rl' : 'auto'};
  font-weight: bold;
  line-height: 1em;
  margin-right: 8px;
`;

const CounterColorBar = (color1: string, color2: string) => css`
  width: 12px;
  height: 50px;
  background: linear-gradient(to bottom, ${color1} 50%, ${color2} 50%);
  margin-right: 8px;
`;

const CounterTime = css`
  display: grid;
  grid-template-columns: repeat(2, minmax(auto, max-content));
`;

const CounterTimeCell = css`
  line-height: 1.5;
`;

const CounterCount = css`
  margin-left: 8px;
`;

const mousePointer = (enable: boolean) => css`
  pointer-events: ${enable ? 'auto' : 'none'};
`;

export const DefecationRatioPieChart: React.FC<Props> = ({ countFecesByTerm }) => {
  const { t } = useTranslation();
  type Keys = keyof typeof countFecesByTerm;
  const chartRef = React.useRef<HighchartsReact.RefObject>(null);
  useChartResizeObserver(chartRef);
  const chartData = Object.entries(DEFECATION_ANALYTICS.COUNT_FECES_BY_TERM).map(([k, v]) => {
    return {
      name: v,
      y: countFecesByTerm[k as Keys] ?? 0,
    };
  });
  const hasNoData = chartData.every((item) => item.y === 0);
  const colors = ['#FFC8D2', '#ED93A3', '#556BA3', '#284283'];
  const options: Highcharts.Options = {
    chart: {
      type: 'pie',
      marginTop: 0,
      marginBottom: 0,
      height: 200,
      backgroundColor: 'transparent',
      spacing: [0, 0, 0, 0],
    },
    title: {
      text: '',
    },
    tooltip: {
      pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>',
    },
    accessibility: {
      point: {
        valueSuffix: '%',
      },
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: 'pointer',
        colors,
        dataLabels: {
          enabled: true,
          formatter: function () {
            return this.y === 0 || hasNoData ? null : `${this.percentage.toFixed(1)}%`;
          },
          distance: -50,
          style: {
            fontSize: '16px',
          },
        },
      },
    },
    series: [
      {
        type: 'pie',
        name: t('ratio', '割合'),
        data: hasNoData ? [{ name: 'No Data', y: 1, color: '#EDEDED' }] : chartData,
      },
    ],
    credits: {
      enabled: false,
    },
  };

  return (
    <div css={GraphContainerStyle}>
      <div css={GraphTitle}>{t('defecationTime', '排便の時間帯')}</div>
      <div css={GraphSubtitle}>{t('soiledDataNotIncluded', '※付着データは含まれていません')}</div>
      <div css={mousePointer(!hasNoData)}>
        <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
      </div>
      <div css={GraphLabel}>
        <DayNightTermCounter countFecesByTerm={countFecesByTerm} />
      </div>
    </div>
  );
};

const DayNightTermCounter = React.memo(
  ({ countFecesByTerm }: { countFecesByTerm: IGetDfreeBDeviceAnalyticsResult['countFecesByTerm'] }) => {
    const { t, i18n } = useTranslation();
    const labels = Object.values(DEFECATION_ANALYTICS.COUNT_FECES_BY_TERM);
    return (
      <div css={CounterContainer}>
        <div css={CounterSection}>
          <div css={CounterLabel(i18n.language)} style={{ color: '#ED93A3' }}>
            {t('daytime', '日中')}
          </div>
          <div css={CounterColorBar('#FFC8D2', '#ED93A3')} />
          <div css={CounterTime}>
            <div css={CounterTimeCell}>{labels[0]}</div>
            <span css={CounterCount}>
              {countFecesByTerm['day1'] ?? 0}
              {t('times', '回')}
            </span>
            <div css={CounterTimeCell}>{labels[1]}</div>
            <span css={CounterCount}>
              {countFecesByTerm['day2'] ?? 0}
              {t('times', '回')}
            </span>
          </div>
        </div>
        <div css={CounterSection}>
          <div css={CounterLabel(i18n.language)} style={{ color: '#284283' }}>
            {t('nighttime', '夜間')}
          </div>
          <div css={CounterColorBar('#556BA3', '#284283')} />
          <div css={CounterTime}>
            <div css={CounterTimeCell}>{labels[2]}</div>
            <span css={CounterCount}>
              {countFecesByTerm['night1'] ?? 0}
              {t('times', '回')}
            </span>
            <div css={CounterTimeCell}>{labels[3]}</div>
            <span css={CounterCount}>
              {countFecesByTerm['night2'] ?? 0}
              {t('times', '回')}
            </span>
          </div>
        </div>
      </div>
    );
  }
);
